import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { PawPrint, Pencil, Heart, Sparkles, TrendingUp } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export default function About() {
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [dogs, setDogs] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});

  const load = () => api.get("/profile").then((r) => setP(r.data));
  useEffect(() => { load(); api.get("/dogs").then((r) => setDogs(r.data)).catch(() => {}); }, []);

  const openEdit = () => { setF({ ...p }); setOpen(true); };

  const save = async () => {
    try {
      await api.put("/profile", f);
      toast.success("Story updated"); setOpen(false); load();
    } catch (e) {
      if (e?.response?.status === 403) toast.error("Only the account owner can edit the story");
      else toast.error("Could not save");
    }
  };

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  if (!p) return <div className="text-muted-foreground">Loading...</div>;

  const Block = ({ icon: Icon, title, text, tone }) => text ? (
    <div className="rounded-2xl bg-card border border-border/70 p-6 shadow-sm">
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center mb-3 ${tone}`}><Icon className="h-5 w-5" /></div>
      <h3 className="font-head font-semibold mb-1.5">{title}</h3>
      <p className="text-sm text-foreground/80 whitespace-pre-wrap leading-relaxed">{text}</p>
    </div>
  ) : null;

  return (
    <div data-testid="about-page">
      <div className="flex justify-end mb-4">
        {user?.is_admin && <Button data-testid="edit-story" variant="outline" onClick={openEdit} className="rounded-full"><Pencil className="h-4 w-4 mr-1" /> Edit my story</Button>}
      </div>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-3xl overflow-hidden border border-border/70 bg-gradient-to-br from-primary/10 to-accent/10 shadow-sm mb-8">
        <div className="grid md:grid-cols-5">
          <div className="md:col-span-2 h-64 md:h-auto bg-muted">
            {p.photo_url ? <img src={p.photo_url} alt={p.name} className="h-full w-full object-cover" />
              : <div className="h-full w-full flex items-center justify-center min-h-[16rem]"><PawPrint className="h-14 w-14 text-muted-foreground/40" /></div>}
          </div>
          <div className="md:col-span-3 p-8 flex flex-col justify-center">
            <div className="flex items-center gap-2 text-secondary text-sm font-medium mb-2"><Heart className="h-4 w-4" /> My Story</div>
            <h1 className="font-head text-4xl sm:text-5xl font-extrabold tracking-tight">{p.name || "About me & my pack"}</h1>
            <p className="text-lg text-muted-foreground mt-2">{p.headline}</p>
            <p className="text-foreground/85 mt-5 whitespace-pre-wrap leading-relaxed">{p.story}</p>
          </div>
        </div>
      </motion.div>

      {dogs.length > 0 && (
        <div className="mb-8">
          <h2 className="font-head text-2xl font-bold tracking-tight mb-4">My pack ({dogs.length})</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {dogs.map((d) => (
              <div key={d.id} className="rounded-2xl bg-card border border-border/70 overflow-hidden shadow-sm">
                <div className="h-28 bg-muted">
                  {d.photo_url ? <img src={d.photo_url} alt={d.name} className="h-full w-full object-cover" />
                    : <div className="h-full w-full flex items-center justify-center"><PawPrint className="h-7 w-7 text-muted-foreground/40" /></div>}
                </div>
                <div className="p-3">
                  <div className="font-head font-semibold text-sm truncate">{d.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{d.breed || "Mixed"} {d.pack_role ? `· ${d.pack_role}` : ""}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-5">
        <Block icon={PawPrint} title="About my dogs" text={p.dogs_intro} tone="bg-primary/15 text-primary" />
        <Block icon={TrendingUp} title="What I've learned" text={p.learning} tone="bg-accent/20 text-accent-foreground" />
        <Block icon={Sparkles} title="What I'm still working on" text={p.struggles} tone="bg-secondary/15 text-secondary" />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-2xl max-h-[88vh] overflow-y-auto scroll-thin">
          <DialogHeader><DialogTitle className="font-head">Edit my story</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Your name</Label><Input data-testid="story-name" value={f.name || ""} onChange={set("name")} placeholder="e.g. Alex" /></div>
            <div><Label>Headline</Label><Input data-testid="story-headline" value={f.headline || ""} onChange={set("headline")} /></div>
            <div><Label>Photo URL</Label><Input data-testid="story-photo" value={f.photo_url || ""} onChange={set("photo_url")} placeholder="https://... (you with your dogs)" /></div>
            <div><Label>Your story / backstory</Label><Textarea data-testid="story-text" rows={5} value={f.story || ""} onChange={set("story")} placeholder="How you came to have your 4 dogs, your journey managing the pack..." /></div>
            <div><Label>About my dogs</Label><Textarea data-testid="story-dogs" rows={3} value={f.dogs_intro || ""} onChange={set("dogs_intro")} placeholder="Introduce your dogs and their personalities..." /></div>
            <div><Label>What I've learned</Label><Textarea data-testid="story-learning" rows={3} value={f.learning || ""} onChange={set("learning")} /></div>
            <div><Label>What I'm still working on / struggling with</Label><Textarea data-testid="story-struggles" rows={3} value={f.struggles || ""} onChange={set("struggles")} placeholder="Behaviours across the pack you're still figuring out..." /></div>
            <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="save-story" onClick={save} className="rounded-full">Save</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
